import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  Checkbox,
  Divider,
  Icon,
  Image,
  Label,
  Menu,
  Message,
  Tab,
  TabPane,
  Table,
} from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";

import api from "api";
import util from "utils/utils";

import ConfigurableTable from "components/lib/ConfigurableTable";

import type { PermissionCategory, PermissionGroup } from ".";

type RoleEditorPermissionsProps = {
  removePermissionsFromRole: (roleId: string, permissions: string[]) => void;
  addPermissionsToRole: (roleId: string, permissions: string[]) => void;
  forType: OpenAPI.Schemas["Role"]["forType"];
  permissionGroups: PermissionGroup[];
  forId: string;
  usingRole: OpenAPI.Schemas["Role"] & OpenAPI.Schemas["AssignRoleInformation"];
  setRoles: Dispatch<
    SetStateAction<
      Array<OpenAPI.Schemas["Role"] & OpenAPI.Schemas["AssignRoleInformation"]>
    >
  >;
};

type PermissionTableProps = {
  permissionCategories: PermissionCategory[];
  usingRole: OpenAPI.Schemas["Role"] & OpenAPI.Schemas["AssignRoleInformation"];
  toggleIndividualPermission: (
    permission: OpenAPI.Schemas["Role"]["permissions"][number],
  ) => void;
};

export const PermissionTable = ({
  permissionCategories,
  usingRole,
  toggleIndividualPermission,
}: PermissionTableProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const lowerSearchQuery = searchQuery.toLowerCase();

  const { t } = useTranslation();

  return (
    <>
      <Input
        placeholder="Search for permissions..."
        value={searchQuery}
        onChange={(e, { value }) => setSearchQuery(value)}
        icon="search"
        style={{ marginBottom: 12 }}
      />
      <Table compact style={{ marginTop: 0 }}>
        {permissionCategories.map((category, i) => {
          const filteredPermissions = category.permissions.filter(
            (perm) =>
              t(perm.title).toLowerCase().includes(lowerSearchQuery) ||
              t(perm.text).toLowerCase().includes(lowerSearchQuery),
          );
          if (filteredPermissions.length === 0) return null;

          return (
            <React.Fragment key={category.name}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    colSpan={2}
                    style={{
                      padding: "7px 12px",
                      borderTop:
                        i !== 0 ? "1px solid rgba(34, 36, 38, .1)" : "none",
                    }}
                  >
                    {t("common:capitalise", { key: category.name })}
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{
                      padding: "7px 12px",
                      borderTop:
                        i !== 0 ? "1px solid rgba(34, 36, 38, .1)" : "none",
                    }}
                  ></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredPermissions.map((permission) => (
                  <Table.Row key={permission.key}>
                    <Table.Cell collapsing>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          disabled={usingRole?.preventUserEditing}
                          toggle
                          id={`permission-${permission.key}`}
                          checked={usingRole?.permissions.includes(
                            permission.key,
                          )}
                          onChange={() =>
                            toggleIndividualPermission(permission.key)
                          }
                        />
                      </div>
                    </Table.Cell>
                    <Table.Cell collapsing>
                      <label htmlFor={`permission-${permission.key}`}>
                        <span style={{ fontWeight: 600, marginRight: 16 }}>
                          {t(permission.title)}
                        </span>
                      </label>
                    </Table.Cell>
                    <Table.Cell>{t(permission.text)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </React.Fragment>
          );
        })}
      </Table>
    </>
  );
};

const RoleEditorPermissions = ({
  addPermissionsToRole,
  removePermissionsFromRole,
  forType,
  forId,
  permissionGroups,
  setRoles,
  usingRole,
}: RoleEditorPermissionsProps) => {
  const { t } = useTranslation();
  const usingForId = forType === "global" ? "global" : forId;

  // Global roles can specify the organisations they're being used for, when the role is just specifically about ghosting
  const showOrgSelector =
    forType === "global" && !usingRole?.preventUserEditing;
  const [orgState, setOrgState] = useState<{
    loading: boolean;
    organisations: OpenAPI.Schemas["Organisation"][];
    page: number;
    query: string;
    total: number;
    nextPageAvailable: boolean;
    previousPageAvailable: boolean;
  }>({
    loading: false,
    organisations: [],
    page: 1,
    query: "",
    total: 0,
    nextPageAvailable: false,
    previousPageAvailable: false,
  });

  // Functions for handling org selection
  const fetchOrganisations = useCallback(() => {
    if (showOrgSelector) {
      setOrgState((prevOrgState) => ({ ...prevOrgState, loading: true }));
      api.organisations.getAll(
        { page: orgState.page, query: orgState.query },
        (data) => {
          setOrgState((prevOrgState) => ({
            ...prevOrgState,
            loading: false,
            organisations: data.organisations,
            total: data.total,
            nextPageAvailable: data.nextPageAvailable,
            previousPageAvailable: data.previousPageAvailable,
          }));
        },
        () => {
          setOrgState((prevOrgState) => ({ ...prevOrgState, loading: false }));
        },
      );
    }
  }, [orgState.page, orgState.query, showOrgSelector]);

  useEffect(() => {
    fetchOrganisations();
  }, [fetchOrganisations]);

  const onSelectOrg = useCallback(
    (organisation) => {
      const organisationId = organisation._id;
      const currentRoleBeingEdited = usingRole;
      if (!currentRoleBeingEdited) return;
      const currentRoleGlobalForOrgIds = [
        ...(currentRoleBeingEdited?.scopes?.organisations ?? []),
      ];
      const currentRoleGlobalForOrgs = [
        ...(currentRoleBeingEdited?.ownerScopes?.organisations ?? []),
      ];
      const orgIsSelected = currentRoleGlobalForOrgIds.includes(organisationId);
      let updatedGlobalForOrgIds: string[] = [];
      let updatedGlobalForOrgs: {
        _id?: string;
        name?: string;
        code?: string;
      }[] = [];
      if (orgIsSelected) {
        updatedGlobalForOrgIds = currentRoleGlobalForOrgIds.filter(
          (orgId) => orgId !== organisationId,
        );
        updatedGlobalForOrgs = currentRoleGlobalForOrgs.filter(
          (org) => org._id !== organisationId,
        );
      } else {
        updatedGlobalForOrgIds = [
          ...currentRoleGlobalForOrgIds,
          organisationId,
        ];
        updatedGlobalForOrgs = [...currentRoleGlobalForOrgs, organisation];
      }
      setRoles((prevRoles) =>
        prevRoles.map((role) =>
          role._id === currentRoleBeingEdited._id ?
            {
              ...role,
              scopes: {
                ...(role?.scopes ?? {}),
                organisations: updatedGlobalForOrgIds,
              },
              ownerScopes: {
                ...(role?.ownerScopes ?? {}),
                organisations: updatedGlobalForOrgs,
              },
            }
          : role,
        ),
      );
      api.roles.updateRole(
        forType,
        usingForId,
        currentRoleBeingEdited._id,
        { scopes: { organisations: updatedGlobalForOrgIds } },
        () => {},
        (err) => {
          toast.error(err.message);
          setRoles((prevRoles) =>
            prevRoles.map((role) =>
              role._id === currentRoleBeingEdited._id ?
                {
                  ...role,
                  scopes: {
                    ...(role?.scopes ?? {}),
                    organisations: currentRoleGlobalForOrgIds,
                  },
                  ownerScopes: {
                    ...(role?.ownerScopes ?? {}),
                    organisations: currentRoleGlobalForOrgs,
                  },
                }
              : role,
            ),
          );
        },
      );
    },
    [usingRole, setRoles, forType, usingForId],
  );

  const getPermissionDetail = useCallback(
    (permissionKey: string) => {
      for (const group of permissionGroups) {
        const found = group.permissions
          .flatMap((category) => category.permissions)
          .find((perm) => perm.key === permissionKey);
        if (found) return found;
      }
      return undefined;
    },
    [permissionGroups],
  );

  const removeSinglePermissionFromRole = useCallback(
    (
      roleId: string,
      permission: OpenAPI.Schemas["Role"]["permissions"][number],
    ) => {
      const permissionDetail = getPermissionDetail(permission);
      util
        .confirm(
          `Removing "${t(permissionDetail?.title || permission)}" from "${usingRole.name}"`,
          "Are you sure you want to remove this permission from this role? You are able to add it back at any time.",
        )
        .then(() => {
          removePermissionsFromRole(roleId, [permission]);
        })
        .catch(() => {});
    },
    [getPermissionDetail, removePermissionsFromRole, usingRole, t],
  );

  const toggleIndividualPermission = useCallback(
    (permission: OpenAPI.Schemas["Role"]["permissions"][number]) => {
      if (!usingRole) return;
      if (usingRole.permissions.includes(permission)) {
        removeSinglePermissionFromRole(usingRole._id, permission);
      } else {
        addPermissionsToRole(usingRole._id, [permission]);
      }
    },
    [addPermissionsToRole, usingRole, removeSinglePermissionFromRole],
  );

  const permissionKeys = Object.keys(permissionGroups);

  return (
    <>
      {usingRole?.preventUserEditing ?
        <div>
          <Message info>
            <Icon name="info" />
            This role is a system role, its permissions cannot be edited. It is
            required for the system to function correctly.
          </Message>
          <Divider hidden />
        </div>
      : null}
      {permissionKeys?.length === 1 ?
        <div
          style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}
        >
          <PermissionTable
            permissionCategories={
              permissionGroups[permissionKeys[0]].permissions
            }
            usingRole={usingRole}
            toggleIndividualPermission={toggleIndividualPermission}
          />
        </div>
      : <Tab
          panes={permissionGroups.map((group) => ({
            menuItem: (
              <Menu.Item>
                {group.title}
                <Label size="tiny">
                  {
                    usingRole.permissions.filter((p) =>
                      group.permissions.some((category) =>
                        category.permissions.some((perm) => perm.key === p),
                      ),
                    ).length
                  }
                  /
                  {group.permissions.reduce(
                    (acc, category) => acc + category.permissions.length,
                    0,
                  )}
                </Label>
              </Menu.Item>
            ),
            render: () => (
              <TabPane>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    flex: 1,
                  }}
                >
                  <PermissionTable
                    permissionCategories={group.permissions}
                    usingRole={usingRole}
                    toggleIndividualPermission={toggleIndividualPermission}
                  />
                </div>
              </TabPane>
            ),
          }))}
        />
      }

      {showOrgSelector ?
        <div>
          <Divider hidden />
          <h5>Limit to specific organisations</h5>
          <p>
            Choose organisations that these permissions should be restricted to.
            Leave these empty to allow unrestricted superadmin access.
          </p>

          <Input
            placeholder="Search for organisations by name/code.."
            value={orgState.query}
            onChange={(e) =>
              setOrgState((prevOrgState) => ({
                ...prevOrgState,
                query: e.target.value,
              }))
            }
            fluid
            style={{ marginBottom: 12 }}
          />

          <ConfigurableTable
            onSelect={(org) => onSelectOrg(org)}
            selectedKeys={usingRole?.scopes?.organisations ?? []}
            tableKey="superadmin-organisations"
            data={orgState.organisations}
            keyExtractor={(org) => org._id}
            preventSelectAll
            columns={[
              {
                key: "darkLogoUrl",
                name: "",
                settingName: "Image",
                center: true,
                width: 40,
                render: ({ cell }) => (
                  <Image style={{ objectFit: "contain" }} avatar src={cell} />
                ),
              },
              { key: "name", name: `Organisation (${orgState.total})` },
              {
                key: "code",
                name: "Code",
                render: ({ cell }) => (
                  <span
                    style={{
                      background: "#efefef",
                      fontSize: "11px",
                      display: "flex",
                      justifyContent: "center",
                      alignContent: "center",
                      borderRadius: 3,
                      padding: "0 3px",
                    }}
                  >
                    {cell}
                  </span>
                ),
              },
            ]}
          />
        </div>
      : null}
    </>
  );
};

export default RoleEditorPermissions;
